<template>
  <div class="patient-select-input" ref="container">
    <!-- Preview Mode (when not focused) -->
    <div
      v-if="!isOpen"
      class="select-preview"
      :class="{ disabled: disabled }"
      @click="!disabled && openDropdown()"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="!disabled && openDropdown()"
      @keydown.space.prevent="!disabled && openDropdown()"
    >
      <div v-if="displayPatient" class="preview-content">
        <IconCircle :initials="displayPatient.initials" :bg-color="iconBgColor" :color="iconFgColor" :size="22" />
        <span class="preview-text">{{ displayPatient.displayName }}</span>
      </div>
      <span v-else class="placeholder-text">Select patient</span>
    </div>

    <!-- Input Mode (when focused) -->
    <div v-else class="select-active">
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="text"
        class="search-input"
        placeholder="Search patients..."
        @input="handleSearch"
        @keydown.esc="closeDropdown"
        @blur="handleBlur"
      />
    </div>

    <!-- Dropdown Menu (teleported to body) -->
    <Teleport to="body">
      <div
        v-if="isOpen && dropdownPosition"
        class="dropdown-menu"
        :style="{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`
        }"
      >
        <div v-if="loading" class="dropdown-item loading-item">
          Loading...
        </div>
        <div v-else-if="patients.length === 0" class="dropdown-item empty-item">
          No patients found
        </div>
        <button
          v-else
          v-for="patient in patients"
          :key="patient.patientId"
          class="dropdown-item"
          @mousedown.prevent="selectPatient(patient)"
        >
          <IconCircle :initials="getInitials(patient)" :bg-color="iconBgColor" :color="iconFgColor" :size="22" />
          <span class="item-text">{{ getDisplayName(patient) }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import * as api from '@/api'
import { queryClient } from '@/libs/queryClient'
import IconCircle from '@/components/common/IconCircle.vue'

export default {
  name: 'PatientSelectInput',
  components: { IconCircle },
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchTerm: '',
      patients: [],
      displayPatient: null,
      loading: false,
      debounceTimer: null,
      dropdownPosition: null,
      iconBgColor: '#e1edff',
      iconFgColor: '#406396'
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.displayPatient) {
          this.fetchPatientForPreview(newVal)
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true)
    window.addEventListener('resize', this.updateDropdownPosition)
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer)
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
  },
  methods: {
    async openDropdown() {
      this.isOpen = true
      this.searchTerm = ''
      this.updateDropdownPosition()
      await this.fetchPatients()
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
    },
    closeDropdown() {
      this.isOpen = false
      this.searchTerm = ''
      this.patients = []
      this.dropdownPosition = null
    },
    updateDropdownPosition() {
      if (!this.isOpen || !this.$refs.container) return

      const rect = this.$refs.container.getBoundingClientRect()
      const dropdownMaxHeight = 300
      const gap = 4
      const viewportHeight = window.innerHeight

      // Check if dropdown would overflow below
      const spaceBelow = viewportHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap

      let top
      if (spaceBelow >= dropdownMaxHeight || spaceBelow >= spaceAbove) {
        // Position below
        top = rect.bottom + gap
      } else {
        // Position above
        top = rect.top - gap - Math.min(dropdownMaxHeight, spaceAbove)
      }

      this.dropdownPosition = {
        top,
        left: rect.left,
        width: rect.width
      }
    },
    handleBlur() {
      // Delay to allow click events to fire
      setTimeout(() => {
        this.closeDropdown()
      }, 200)
    },
    handleSearch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetchPatients()
      }, 300)
    },
    async fetchPatients() {
      this.loading = true
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['patients', { search: this.searchTerm, limit: 50 }],
          queryFn: () => api.fetchPatients({
            search: this.searchTerm,
            limit: 50,
            offset: 0
          })
        })
        this.patients = response.patients || []
      } catch (error) {
        console.error('Error fetching patients:', error)
        this.patients = []
      } finally {
        this.loading = false
      }
    },
    async fetchPatientForPreview(patientId) {
      if (!patientId) {
        this.displayPatient = null
        return
      }

      try {
        const patient = await queryClient.fetchQuery({
          queryKey: ['patient', patientId],
          queryFn: () => api.fetchPatientById(patientId)
        })
        this.displayPatient = {
          ...patient,
          initials: this.getInitials(patient),
          displayName: this.getDisplayName(patient)
        }
      } catch (error) {
        console.error('Error fetching patient for preview:', error)
        this.displayPatient = null
      }
    },
    selectPatient(patient) {
      this.$emit('update:modelValue', patient.patientId)
      this.displayPatient = {
        ...patient,
        initials: this.getInitials(patient),
        displayName: this.getDisplayName(patient)
      }
      this.closeDropdown()
    },
    getInitials(patient) {
      const first = patient.firstName?.[0] || ''
      const last = patient.lastName?.[0] || ''
      return (first + last).toUpperCase() || 'U'
    },
    getDisplayName(patient) {
      const firstName = patient.firstName || ''
      const lastName = patient.lastName || ''
      return `${firstName} ${lastName}`.trim() || 'Unknown'
    }
  }
}
</script>

<style scoped>
.patient-select-input {
  width: 100%;
}

/* Preview Mode Styles */
.select-preview {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
  height: 31px;
  display: flex;
  align-items: center;
}

.select-preview:hover {
  border-color: #9ca3af;
}

.select-preview:focus {
  outline: none;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

.select-preview.disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
}

.select-preview.disabled .preview-text,
.select-preview.disabled .placeholder-text {
  color: #9ca3af;
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.preview-text {
  font-size: 14px;
  color: #333;
  line-height: 1;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
}

/* Active Mode Styles */
.search-input {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #4338ca;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
  height: 31px;
}

.search-input:focus {
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Dropdown Menu (Teleported) */
.dropdown-menu {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 14px;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
}

.dropdown-item:first-child {
  border-radius: 0 8px 0 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 8px 8px;
}

.loading-item,
.empty-item {
  color: #9ca3af;
  font-style: italic;
  cursor: default;
}

.loading-item:hover,
.empty-item:hover {
  background-color: transparent;
}
</style>
